import { marked } from "marked";
import { makePathRegex, parseCandidate } from "./utils/fileRefDetect";

marked.use({ gfm: true, breaks: true });

// Render LLM markdown output into HTML safe to feed into
// dangerouslySetInnerHTML. Ports the sanitizer from apps/web/app.js so the
// React workbench gets the same tag/attribute allowlist:
//   - Allowed: p, br, headings, lists, code, pre, blockquote, anchors,
//     tables, plus inline formatting.
//   - Dropped (with content): script, style, iframe, object, embed, link,
//     meta, base.
//   - Unknown tags are unwrapped (children kept).
//   - All attributes are removed except <a href> (when safe) and <a title>.
//   - on*/style attributes are always stripped.
//   - <a href> values are validated against an http/https/mailto/anchor/
//     same-origin allowlist; safe links get rel="noopener noreferrer"
//     target="_blank" added.
//
// Without this, the marked.parse() output goes straight into innerHTML and
// an attacker-controlled assistant could inject scripts via tool output,
// PR diffs, or any other content stream rendered through the chat.

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "code",
  "pre",
  "ul",
  "ol",
  "li",
  "blockquote",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
]);

const DROP_WITH_CONTENT = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "link",
  "meta",
  "base",
]);

function isSafeHref(value: string): boolean {
  const href = String(value || "").trim();
  if (!href) return false;
  if (href.startsWith("#") || href.startsWith("/")) return true;
  try {
    const url = new URL(href, window.location.origin);
    return ["http:", "https:", "mailto:"].includes(url.protocol);
  } catch {
    return false;
  }
}

function sanitizeFragment(root: DocumentFragment): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  const elements: Element[] = [];
  while (walker.nextNode()) elements.push(walker.currentNode as Element);

  for (const el of elements) {
    const tag = el.tagName.toLowerCase();
    if (DROP_WITH_CONTENT.has(tag)) {
      el.remove();
      continue;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || name === "style") {
        el.removeAttribute(attr.name);
        continue;
      }
      if (tag === "a" && name === "href") {
        if (isSafeHref(attr.value)) {
          el.setAttribute("rel", "noopener noreferrer");
          el.setAttribute("target", "_blank");
        } else {
          el.removeAttribute(attr.name);
        }
        continue;
      }
      if (!(tag === "a" && name === "title")) el.removeAttribute(attr.name);
    }
  }
}

export function renderSafeMarkdown(source: string): string {
  if (!source) return "";
  let html: string;
  try {
    html = marked.parse(source, { async: false });
  } catch {
    return escapeHtml(source);
  }
  // In a jsdom test environment `document` is available; in any browser
  // execution path it always is. The template-element technique parses HTML
  // without executing scripts, which is exactly what we need before
  // walking the tree to strip dangerous nodes.
  const template = document.createElement("template");
  template.innerHTML = html;
  sanitizeFragment(template.content);
  return template.innerHTML;
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    if (char === "&") return "&amp;";
    if (char === "<") return "&lt;";
    if (char === ">") return "&gt;";
    if (char === '"') return "&quot;";
    return "&#39;";
  });
}

// Wikilink post-process: turn [[Title]] in already-sanitized HTML into a
// styled anchor with a data-target the page handler can route on. Runs
// AFTER renderSafeMarkdown's sanitizer so the static <a class="wikilink"
// data-target="..."> template is trusted (built from escaped input here).
//
// Skips content inside <code>…</code> and <pre>…</pre> so that fenced
// code samples render `[[Foo]]` verbatim instead of becoming a clickable
// link inside the code block. Without the skip, a bash example like
// `# see [[Runbook]]` would navigate the page when clicked, and any
// future relaxation of <code> attribute sanitization would become a
// straight injection vector via this post-processor.
function transformOutsideCode(html: string, transform: (chunk: string) => string): string {
  const re = /<(code|pre)\b[^>]*>[\s\S]*?<\/\1>/gi;
  let out = "";
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    out += transform(html.slice(cursor, m.index));
    out += m[0]; // preserve code/pre block verbatim
    cursor = m.index + m[0].length;
  }
  out += transform(html.slice(cursor));
  return out;
}

function wikilinkifyHtml(html: string): string {
  return transformOutsideCode(html, (chunk) =>
    chunk.replace(/\[\[([^\]\n]+?)\]\]/g, (_, title) => {
      const t = String(title).trim();
      const safe = t
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
      return `<a class="wikilink" data-target="${safe}">${safe}</a>`;
    })
  );
}

/**
 * Render markdown with [[wikilink]] post-processing. Built on top of
 * renderSafeMarkdown so the underlying tag/attribute allowlist still applies
 * to the user-authored markdown, and wikilinks are added after sanitization
 * from a static, HTML-escaped template.
 */
export function renderMarkdownWithWikilinks(source: string): string {
  return wikilinkifyHtml(renderSafeMarkdown(source));
}

// Transform text OUTSIDE <pre>…</pre> and <a>…</a> blocks. We linkify inside
// inline <code> (where agents usually write `src/x.js`), but never inside
// fenced code samples (<pre>) or existing anchors (avoids nested <a> + href
// contamination). Mirrors the wikilink transformOutsideCode helper.
function transformOutsidePre(html: string, transform: (chunk: string) => string): string {
  const re = /<(pre|a)\b[^>]*>[\s\S]*?<\/\1>/gi;
  let out = "";
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    out += transform(html.slice(cursor, m.index));
    out += m[0];
    cursor = m.index + m[0].length;
  }
  out += transform(html.slice(cursor));
  return out;
}

function filelinkifyHtml(html: string): string {
  return transformOutsidePre(html, (chunk) => {
    // Safety valve: the path regex is O(n^2) on a long run of path-class chars
    // with no slash (agent-controllable, e.g. a base64 blob). Never let it
    // block the render — skip linkify for very large chunks.
    if (chunk.length > 8192) return chunk;
    const re = makePathRegex();
    let out = "";
    let cursor = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(chunk))) {
      const before = m.index > 0 ? chunk[m.index - 1] : null;
      const cand = parseCandidate(m[0], m.index, before);
      if (!cand) continue;
      out += chunk.slice(cursor, m.index);
      const path = escapeHtml(cand.path);
      const lineAttr = cand.line != null ? ` data-line="${cand.line}"` : "";
      out += `<span class="file-link" data-path="${path}"${lineAttr}>${escapeHtml(cand.raw)}</span>`;
      cursor = m.index + m[0].length;
    }
    out += chunk.slice(cursor);
    return out;
  });
}

/**
 * Render markdown, then wrap resolvable-looking file paths (outside fenced
 * code / anchors) in <span class="file-link" data-path data-line>. Resolution
 * to a real file happens later in ChatPanel; unresolved spans are inert.
 */
export function renderMarkdownWithFileLinks(source: string): string {
  return filelinkifyHtml(renderSafeMarkdown(source));
}
